import React, { useEffect, useMemo, useRef, useState } from "react";
import { useUrbanAnimation } from "../../contexts/UrbanAnimationContext";
import { useMap } from "../../contexts/MapContext";
import { urbanEdgeOpacity } from "./urbanEdgeFade";
import type * as mapboxgl from "mapbox-gl";

// Map.tsx removes the map inside an effect cleanup and only clears the
// context on the following render, so the timers and handlers below can fire
// against a dead instance whose style is already gone.  mapbox then throws
// "Cannot read properties of undefined (reading 'getOwnSource')" from
// getSource (mitigate_v2#177).  `_removed` is set by `map.remove()`.
const isAlive = (map: mapboxgl.Map) => !map._removed;

// True once the style JSON has loaded, which is all addSource/addLayer need
// (they throw "Style is not done loading" only before then).  Not
// `isStyleLoaded()`: that also waits for every source's tiles, and on the
// project page the fire animation's canvas source (`animate: true`) keeps the
// map repainting, so the `idle` that would end that wait never fires and the
// arcs were never set up.  `_loaded` is set just before `style.load` fires.
const isStyleParsed = (map: mapboxgl.Map) => !!map.style?._loaded;

interface IgnitionEdge {
  src: number | null;
  dst: number | null;
  mode: "conv" | "rad" | "ember" | "cond" | "wildland-rad" | "wildland-ember" | string;
  t: number;
  src_node: number | null;
  dst_node: number;
  kind?: "encroach" | "exit" | "internal" | "wildland";
  // [lng, lat] origin for sourceless "wildland" (bushfire→home) edges.
  src_pos?: [number, number] | null;
  // What each end is (building / tree / shrub); crown ends have null src/dst
  // ids and are placed from node positions.
  src_kind?: "building" | "tree" | "shrub" | "interface" | null;
  dst_kind?: "building" | "tree" | "shrub" | "interface";
}

const SOURCE_ID = "urban-ignition-edges";
const SOLID_LAYER_ID = "urban-ignition-edges-solid";
const EMBER_LAYER_ID = "urban-ignition-edges-ember";
const ARROW_LAYER_ID = "urban-ignition-edges-arrow";
const ARROW_IMAGE_ID = "urban-ignition-edges-arrow-icon";
const ASSET_SOURCE_ID = "urban-building-state";
const ASSET_SOURCE_LAYER = "assets";

// Build a small filled triangle pointing right (+x).  Registered as an SDF
// icon so mapbox can tint it per-feature via ``icon-color`` — letting every
// arrow inherit its arc's mode colour without us baking four separate
// images.  Mapbox rotates the icon along the line via ``symbol-placement:
// 'line-center'`` so it points in the direction of travel.
const buildArrowImage = (): ImageData | null => {
  if (typeof document === "undefined") return null;
  const size = 16;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.clearRect(0, 0, size, size);
  ctx.fillStyle = "#ffffff";
  ctx.beginPath();
  ctx.moveTo(2, 3);
  ctx.lineTo(size - 2, size / 2);
  ctx.lineTo(2, size - 3);
  ctx.closePath();
  ctx.fill();
  return ctx.getImageData(0, 0, size, size);
};

const featureCentroid = (feature: any): [number, number] | null => {
  const geom = feature?.geometry;
  if (!geom) return null;
  let ring: number[][] | null = null;
  if (geom.type === "Polygon") ring = geom.coordinates[0];
  else if (geom.type === "MultiPolygon") ring = geom.coordinates[0][0];
  else if (geom.type === "Point") return [geom.coordinates[0], geom.coordinates[1]];
  if (!ring?.length) return null;
  const lng = ring.reduce((s, p) => s + p[0], 0) / ring.length;
  const lat = ring.reduce((s, p) => s + p[1], 0) / ring.length;
  return [lng, lat];
};

const quadraticArc = (
  p1: [number, number],
  p2: [number, number],
  curvature = 0.35,
  steps = 24,
): [number, number][] => {
  const mx = (p1[0] + p2[0]) / 2;
  const my = (p1[1] + p2[1]) / 2;
  const dx = p2[0] - p1[0];
  const dy = p2[1] - p1[1];
  const cpx = mx - dy * curvature;
  const cpy = my + dx * curvature;
  const pts: [number, number][] = [];
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const u = 1 - t;
    pts.push([u * u * p1[0] + 2 * u * t * cpx + t * t * p2[0], u * u * p1[1] + 2 * u * t * cpy + t * t * p2[1]]);
  }
  return pts;
};

const buildEdgesSource = (
  edges: IgnitionEdge[],
  nodePositions: Record<number, [number, number]>,
  frameElapsedS: number,
  useNodePositions: boolean,
  centroidMap: Map<string, [number, number]>,
) => {
  const features: any[] = [];

  for (const edge of edges) {
    if (edge.t > frameElapsedS) continue;
    // Legacy interface edges are intentionally not rendered.  The new
    // "wildland" edges (bushfire → home, radiant / ember) ARE rendered below.
    if (edge.kind === "encroach" || edge.kind === "exit") continue;

    let coordinates: [number, number][];

    if (edge.kind === "wildland" && edge.src_pos) {
      // Sourceless bushfire→home arrow: the source is the nearest fireline cell
      // (lng/lat), the destination is the structure.  Gentler curvature since
      // an ember edge can be long.
      let dstPos: [number, number] | null = useNodePositions
        ? nodePositions[edge.dst_node] ?? null
        : edge.dst != null
          ? centroidMap.get(String(edge.dst)) ?? null
          : null;
      if (!dstPos) continue;
      coordinates = quadraticArc(edge.src_pos, dstPos, 0.2);
    } else if (useNodePositions) {
      // New format: use node positions
      const dstPos = nodePositions[edge.dst_node];
      if (!dstPos) continue;
      const srcPos = edge.src_node !== null ? nodePositions[edge.src_node] : null;

      if (srcPos) {
        coordinates = quadraticArc(srcPos, dstPos);
      } else {
        const offset = 0.00025;
        coordinates = quadraticArc(
          [dstPos[0] - offset, dstPos[1] + offset],
          dstPos,
          0.6,
        );
      }
    } else {
      // Old format: use centroid map
      if (edge.dst == null) continue;
      const dstId = String(edge.dst);
      const dstCentroid = centroidMap.get(dstId);
      if (!dstCentroid) continue;

      const srcId = edge.src != null ? String(edge.src) : null;
      const srcCentroid = srcId ? centroidMap.get(srcId) : null;

      if (srcCentroid) {
        coordinates = quadraticArc(srcCentroid, dstCentroid);
      } else {
        const offset = 0.00025;
        coordinates = quadraticArc(
          [dstCentroid[0] - offset, dstCentroid[1] + offset],
          dstCentroid,
          0.6,
        );
      }
    }

    features.push({
      type: "Feature",
      properties: {
        mode: edge.mode,
        t: edge.t,
        // Seconds since the arc fired at this frame; the paints fade on it.
        age_s: frameElapsedS - edge.t,
        src_kind: edge.src_kind ?? "building",
        dst_kind: edge.dst_kind ?? "building",
      },
      geometry: { type: "LineString", coordinates },
    });
  }

  return { type: "FeatureCollection", features };
};

const UrbanIgnitionEdgesLayer: React.FC = () => {
  const { currentFrame, timestamps, selectedIgnitionInfo } = useUrbanAnimation();
  const { map } = useMap();

  const edges: IgnitionEdge[] = selectedIgnitionInfo?.urban_ignition_edges ?? [];
  const nodePositions: Record<number, [number, number]> = selectedIgnitionInfo?.urban_node_positions ?? {};
  const hasNodePositions = Object.keys(nodePositions).length > 0;
  const hasUrbanData = edges.length > 0;

  const centroidMapRef = useRef<Map<string, [number, number]>>(new Map());
  const [centroidsReady, setCentroidsReady] = useState(false);

  const frameElapsedS = useMemo(() => {
    if (!timestamps.length || currentFrame === null || currentFrame >= timestamps.length) return null;
    return timestamps[currentFrame].diff(timestamps[0], 'second');
  }, [timestamps, currentFrame]);

  // Build centroid map from asset vector tiles (for old data format)
  useEffect(() => {
    if (!map || hasNodePositions) return; // Skip if we have node positions (new format)

    const buildCentroids = () => {
      if (!isAlive(map) || !map.getSource(ASSET_SOURCE_ID)) return;

      const features = map.querySourceFeatures(ASSET_SOURCE_ID, { sourceLayer: ASSET_SOURCE_LAYER });
      if (features.length === 0) return;
      const newMap = new Map<string, [number, number]>();
      for (const feature of features) {
        const assetId = String((feature as any).id ?? (feature.properties as any)?.__id ?? "");
        const centroid = featureCentroid(feature);
        if (centroid && assetId) newMap.set(assetId, centroid);
      }
      if (newMap.size > 0) {
        centroidMapRef.current = newMap;
        setCentroidsReady(true);
      }
    };

    const checkSourceInterval = setInterval(() => {
      if (!isAlive(map)) {
        clearInterval(checkSourceInterval);
        return;
      }
      if (map.getSource(ASSET_SOURCE_ID)) {
        clearInterval(checkSourceInterval);
        buildCentroids();
      }
    }, 100);

    map.on("sourcedata", buildCentroids);
    return () => {
      clearInterval(checkSourceInterval);
      map.off("sourcedata", buildCentroids);
    };
  }, [map, hasNodePositions]);

  // Flipped once the source + layers exist so the frame-data effect below
  // re-runs and paints the current frame when setup had to be deferred.
  const [sourceReady, setSourceReady] = useState(false);

  // Add source and layers.  Setup waits for the basemap style: opening a
  // project straight onto a simulation with urban ignition edges mounts this
  // layer before the style has finished loading, and mapbox then throws
  // "Style is not done loading" from addSource/addLayer (#373).
  useEffect(() => {
    if (!map || !isAlive(map) || !hasUrbanData) return;
    if (!hasNodePositions && !centroidsReady) return; // Wait for centroids if using old format

    let cancelled = false;

    const setup = () => {
      // Register the SDF arrow icon once.  Using `sdf: true` lets the symbol
      // layer recolour the same image via `icon-color` per feature.
      if (!map.hasImage(ARROW_IMAGE_ID)) {
        const img = buildArrowImage();
        if (img) {
          map.addImage(ARROW_IMAGE_ID, img as any, { sdf: true } as any);
        }
      }

      if (!map.getSource(SOURCE_ID)) {
        map.addSource(SOURCE_ID, {
          type: "geojson",
          data: { type: "FeatureCollection", features: [] },
        } as any);

        map.addLayer({
          id: SOLID_LAYER_ID,
          type: "line",
          source: SOURCE_ID,
          minzoom: 14,
          filter: ["in", ["get", "mode"], ["literal", ["conv", "rad", "cond", "wildland-rad"]]],
          paint: {
            "line-color": [
              "match",
              ["get", "mode"],
              "conv", "#ff6b00",
              // Radiation — same symbology whether house-to-house or bushfire→home.
              "rad", "#ffcc00",
              "wildland-rad", "#ffcc00",
              "#888888",
            ],
            "line-width": ["match", ["get", "mode"], "conv", 3, "rad", 2.5, "wildland-rad", 2.5, 2],
            // Fades with the arc's age (urbanEdgeFade) on top of the zoom fade-in.
            "line-opacity": urbanEdgeOpacity(14, 15.5, 0.9, ["get", "age_s"]),
          },
          layout: { "line-cap": "round", "line-join": "round" },
        } as any);

        map.addLayer({
          id: EMBER_LAYER_ID,
          type: "line",
          source: SOURCE_ID,
          minzoom: 14,
          filter: ["in", ["get", "mode"], ["literal", ["ember", "wildland-ember"]]],
          paint: {
            // Ember — same dashed symbology whether house-to-house or bushfire→home.
            "line-color": "#dc13ff",
            "line-width": 4,
            "line-dasharray": [1, 3],
            "line-opacity": urbanEdgeOpacity(14, 15.5, 1.0, ["get", "age_s"]),
          },
          layout: { "line-cap": "round" },
        } as any);

        // One arrowhead at the midpoint of every arc, rotated along the line.
        // Colour mirrors the arc's mode so the direction marker reads as part
        // of the line; a dark halo keeps it legible on bright basemap tiles.
        //
        // ``minzoom`` skips this layer below z14 — at city-wide zoom levels
        // every arc collapses to a few pixels and the arrowheads turn into a
        // flood of colour.  ``icon-opacity`` then ramps from 0→1 across
        // z14→z15.5 so they fade in instead of popping on at the threshold.
        if (map.hasImage(ARROW_IMAGE_ID)) {
          map.addLayer({
            id: ARROW_LAYER_ID,
            type: "symbol",
            source: SOURCE_ID,
            minzoom: 15.5,
            layout: {
              "symbol-placement": "line-center",
              "icon-image": ARROW_IMAGE_ID,
              "icon-size": 1.25,
              "icon-rotation-alignment": "map",
              "icon-allow-overlap": true,
              "icon-ignore-placement": true,
            },
            paint: {
              "icon-color": [
                "match",
                ["get", "mode"],
                "conv", "#ff6b00",
                "rad", "#ffcc00",
                "wildland-rad", "#ffcc00",
                "ember", "#dc13ff",
                "wildland-ember", "#dc13ff",
                "#888888",
              ],
              "icon-halo-color": "#000000",
              "icon-halo-width": 1,
              "icon-opacity": urbanEdgeOpacity(15.5, 16.5, 1, ["get", "age_s"]),
            },
          } as any);
        }
      }
      setSourceReady(true);
    };

    const setupWhenReady = () => {
      if (cancelled || !isAlive(map) || !isStyleParsed(map)) return;
      map.off("style.load", setupWhenReady);
      setup();
    };

    if (isStyleParsed(map)) {
      setup();
    } else {
      map.on("style.load", setupWhenReady);
    }

    return () => {
      cancelled = true;
      map.off("style.load", setupWhenReady);
    };
  }, [map, hasUrbanData, hasNodePositions, centroidsReady]);

  // Update GeoJSON data on each frame.  We intentionally do *not* early-return
  // on `!hasUrbanData` here: when the user switches to a sim with no urban
  // ignition edges, we still need to push an empty FeatureCollection to wipe
  // the previous sim's edges off the map.  The `if (!source)` guard below
  // handles the case where no urban-bearing sim has ever been selected and
  // therefore the source was never created in the setup effect.
  useEffect(() => {
    if (!map || !isAlive(map) || frameElapsedS === null) return;
    if (hasUrbanData && !hasNodePositions && !centroidsReady) return;

    const source = map.getSource(SOURCE_ID) as any;
    if (!source) return;

    const geojson = buildEdgesSource(edges, nodePositions, frameElapsedS, hasNodePositions, centroidMapRef.current);
    source.setData(geojson);
  }, [map, edges, nodePositions, frameElapsedS, hasUrbanData, hasNodePositions, centroidsReady, sourceReady]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (!map) return;
      try {
        if (map.getLayer(ARROW_LAYER_ID)) map.removeLayer(ARROW_LAYER_ID);
        if (map.getLayer(EMBER_LAYER_ID)) map.removeLayer(EMBER_LAYER_ID);
        if (map.getLayer(SOLID_LAYER_ID)) map.removeLayer(SOLID_LAYER_ID);
        if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);
      } catch {
        /* map may be tearing down */
      }
      // Leave the registered image — other maps in the same session can
      // share it.  If you do want it gone, also call map.removeImage(...).
    };
  }, [map]);

  return null;
};

export default UrbanIgnitionEdgesLayer;
